// =============================================================================
// FILE STATUS HEADER
// status   : ACTIVE (V2 - PREVIEW ONLY) · version 1.0 (FASE 6A)
// protected: NO -> Notifikasi Stok Rendah in-app (JUAL only, region-aware). Catat changelog.
// =============================================================================
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bell, Loader2, AlertTriangle, PackageX, Settings2, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { isOwner } from "@/v2/components/helpers";
import { TableScroll } from "@/v2/components/TableScroll";
import { StockAlertActionButton } from "@/v2/components/StockAlertAction";

const LOC_LABEL = {
  STORE_AREMA: "Store Arema", STORE_BORNEO: "Store Borneo",
  GUDANG_SBY: "Gudang SBY", GUDANG_SMD: "Gudang SMD",
};

export default function StockAlertPage() {
  const { api, me } = useV2Auth();
  const owner = isOwner(me?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defMin, setDefMin] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v2/stock/alerts");
      if (r.ok) setData(await r.json());
      else toast.error("Gagal memuat notifikasi stok");
      if (owner) {
        const t = await api("/api/v2/stock/thresholds");
        if (t.ok) setDefMin(String((await t.json()).default_min_qty));
      }
    } finally { setLoading(false); }
  }, [api, owner]);

  useEffect(() => { load(); }, [load]);

  const saveDefault = async () => {
    setSaving(true);
    try {
      const r = await api("/api/v2/stock/thresholds/default", { method: "PUT", body: JSON.stringify({ min_qty: Number(defMin) }) });
      if (r.ok) { toast.success(`Ambang default disimpan: ${defMin}`); await load(); }
      else toast.error("Gagal menyimpan ambang");
    } finally { setSaving(false); }
  };

  const sendSummary = async () => {
    setSending(true);
    try {
      const r = await api("/api/v2/stock/alerts/send-summary", { method: "POST", body: "{}" });
      const d = await r.json();
      if (r.ok) toast.success("Ringkasan diproses: " + (d.regions || []).map((x) => `${x.region}:${x.action}`).join(", "));
      else toast.error(d.detail || "Gagal mengirim ringkasan");
    } finally { setSending(false); }
  };

  const items = data?.items || [];

  // A-4 / A3-4: aksi per baris — MEMBUKA FORM (bukan membuat dokumen), produk sudah terpilih.
  // Komponen BERSAMA dengan kartu Dashboard (StockAlertActionButton).
  const action = data?.action || null;
  const ActionBtn = ({ a }) => <StockAlertActionButton action={action} productId={a.product_id} />;

  const cardTitle = data?.title || "Notifikasi Stok Rendah";

  return (
    <div className="space-y-6" data-testid="v2-stock-alert-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2" data-testid="v2-stock-title"><Bell className="w-6 h-6" />{cardTitle}</h2>
          <p className="text-sm text-muted-foreground mt-1">Hanya item <b>JUAL</b>. Item MoU tidak dihitung. {me?.region && me.region !== "GLOBAL" ? `Region ${me.region}.` : "Semua region."}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="v2-stock-refresh"><RefreshCw className="w-4 h-4 mr-2" />Muat Ulang</Button>
      </div>

      {/* ringkasan badge */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4"><div className="text-xs text-muted-foreground">Total Alert</div><div className="text-2xl font-semibold" data-testid="v2-stock-total">{data?.total ?? 0}</div></Card>
        <Card className="p-4 border-destructive/40"><div className="text-xs text-destructive flex items-center gap-1"><PackageX className="w-3.5 h-3.5" />Habis (kritis)</div><div className="text-2xl font-semibold" data-testid="v2-stock-critical">{data?.critical ?? 0}</div></Card>
        <Card className="p-4 border-[hsl(var(--badge-warning))]/40"><div className="text-xs text-[hsl(var(--badge-warning))] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Hampir Habis</div><div className="text-2xl font-semibold" data-testid="v2-stock-low">{data?.low ?? 0}</div></Card>
      </div>

      {/* kontrol Owner */}
      {owner && (
        <Card className="p-4 sm:p-5">
          <h3 className="font-semibold flex items-center gap-2 mb-3"><Settings2 className="w-4 h-4" />Pengaturan (Owner)</h3>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 max-w-xs">
              <Label className="text-xs">Ambang default (min qty JUAL)</Label>
              <Input type="number" min="0" value={defMin} onChange={(e) => setDefMin(e.target.value)} data-testid="v2-stock-default-input" />
            </div>
            <Button onClick={saveDefault} disabled={saving} data-testid="v2-stock-save-default">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Simpan Ambang</Button>
            <Button variant="outline" onClick={sendSummary} disabled={sending} data-testid="v2-stock-send-summary"><Send className="w-4 h-4 mr-2" />Kirim Ringkasan Email</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Ringkasan email idempoten (sekali per region/hari) &amp; dikirim ke alamat uji aman.</p>
        </Card>
      )}

      {/* daftar alert */}
      <Card className="p-4 sm:p-5">
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Tidak ada stok JUAL di bawah ambang. 👍</p>
        ) : (
          <>
            {/* mobile cards */}
            <div className="space-y-2 md:hidden">
              {items.map((a, i) => (
                <div key={i} className="rounded-xl border p-3" data-testid="v2-stock-alert-card">
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{a.name}</span>
                    <div className="flex items-center gap-1">
                      <ActionBtn a={a} />
                      <Badge variant={a.level === "critical" ? "danger" : "warning"}>{a.status}</Badge>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{(a.sku ? a.sku + " · " : "")}{LOC_LABEL[a.location] || a.location} · sisa <b>{a.qty}</b> (ambang {a.min_qty})</div>
                </div>
              ))}
            </div>
            {/* desktop table */}
            <div className="hidden md:block">
              <TableScroll>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Produk</TableHead><TableHead>SKU</TableHead><TableHead>Lokasi</TableHead>
                    <TableHead className="text-right">Sisa (JUAL)</TableHead><TableHead className="text-right">Ambang</TableHead><TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {items.map((a, i) => (
                      <TableRow key={i} data-testid="v2-stock-alert-row">
                        <TableCell className="font-medium"><div className="flex items-center gap-2"><ActionBtn a={a} />{a.name}</div></TableCell>
                        <TableCell className="font-mono text-xs">{a.sku || "-"}</TableCell>
                        <TableCell>{LOC_LABEL[a.location] || a.location}</TableCell>
                        <TableCell className="text-right font-semibold">{a.qty}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{a.min_qty}</TableCell>
                        <TableCell><Badge variant={a.level === "critical" ? "danger" : "warning"}>{a.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableScroll>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
